import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Language, translations } from '../thi-translations';
import { normalizeThiLang } from '../thi-lang.util';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  InvoiceSubscriptionPlan,
  InvoiceSubscriptionsV2Service,
  PublicCompanyExtensionPlanItem,
  PublicDocExtensionPlanItem,
  PublicSubscriptionPlanItem,
  PublicTokenExtensionPlanItem,
} from 'src/app/services/invoice-subscriptions-v2.service';
import { getCampaignT } from './thi-campaign-translations';
import { ThiCampaignAttributionService } from './thi-campaign-attribution.service';

interface ComparisonMetricRow {
  label: string;
  cells: string[];
}

@Component({
  selector: 'app-thi-public-pricing',
  templateUrl: './thi-public-pricing.component.html',
  styleUrls: ['./thi-public-pricing.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ThiPublicPricingComponent implements OnInit, OnChanges, OnDestroy {
  @Input() lang: Language = 'ar';
  @Input() showSectionHeader = true;
  @Input() mobileComparisonTabs = false;
  @Input() useCampaignCopy = false;
  @Input() useCampaignAttribution = false;
  @Input() registerLink = '/register';

  @Output() packageSelected = new EventEmitter<number>();
  @Output() pricingVisible = new EventEmitter<void>();

  @ViewChild('pricingAnchor') pricingAnchor?: ElementRef<HTMLElement>;

  landingLang: Language = 'ar';
  t = translations.ar;
  c = getCampaignT('ar');

  plans: InvoiceSubscriptionPlan[] = [];
  plansLoading = true;
  plansError = false;
  popularPlanIndex = 0;
  comparisonMetricRows: ComparisonMetricRow[] = [];
  selectedComparisonIndex = 0;

  private fetchSub?: Subscription;
  private pricingObserver?: IntersectionObserver;
  private pricingViewFired = false;

  constructor(
    private readonly invoiceSubsV2: InvoiceSubscriptionsV2Service,
    private readonly cdr: ChangeDetectorRef,
    private readonly attribution: ThiCampaignAttributionService,
  ) {}

  ngOnInit(): void {
    this.applyLang();
    this.fetchPlans(false);
    this.setupPricingObserver();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['lang']) {
      this.applyLang();
      if (this.plans.length) {
        this.comparisonMetricRows = this.buildComparisonRows(this.plans);
      }
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy(): void {
    this.fetchSub?.unsubscribe();
    this.pricingObserver?.disconnect();
  }

  get arrowIcon(): string {
    return this.landingLang === 'en' ? 'fa fa-arrow-right' : 'fa fa-arrow-left';
  }

  registerParams(planId?: number): Record<string, string> {
    if (this.useCampaignAttribution) {
      return this.attribution.registerQueryParams(planId);
    }
    if (planId != null) {
      return { planId: String(planId) };
    }
    return {};
  }

  onPlanClick(planId: number): void {
    this.packageSelected.emit(planId);
  }

  reloadPlans(): void {
    this.fetchPlans(true);
  }

  isPopular(index: number): boolean {
    return index === this.popularPlanIndex && this.plans.length > 0;
  }

  trackByPlanId(_i: number, p: InvoiceSubscriptionPlan): number {
    return p.id;
  }

  planName(p: InvoiceSubscriptionPlan): string {
    if (this.landingLang === 'ar') {
      return p.name_ar || p.name_en || p.name_he || '';
    }
    if (this.landingLang === 'he') {
      return p.name_he || p.name_en || p.name_ar || '';
    }
    return p.name_en || p.name_ar || p.name_he || '';
  }

  planDesc(p: InvoiceSubscriptionPlan): string {
    if (this.landingLang === 'ar') {
      return p.description_ar || p.description_en || p.description_he || '';
    }
    if (this.landingLang === 'he') {
      return p.description_he || p.description_en || p.description_ar || '';
    }
    return p.description_en || p.description_ar || p.description_he || '';
  }

  planFeatureLines(plan: InvoiceSubscriptionPlan): string[] {
    const d = this.planDesc(plan);
    if (!d.trim()) {
      return [];
    }
    return d
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }

  formatPrice(plan: InvoiceSubscriptionPlan): string {
    const n = this.numericPrice(plan.price);
    const formatted = Number.isFinite(n)
      ? n.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : '';
    const suffix = this.pricePeriodSuffix(plan.duration_days);
    return suffix ? `${formatted}${suffix}` : formatted;
  }

  private applyLang(): void {
    this.landingLang = normalizeThiLang(this.lang as unknown as string);
    this.t = translations[this.landingLang];
    this.c = getCampaignT(this.landingLang);
  }

  private setupPricingObserver(): void {
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }
    setTimeout(() => {
      const el = this.pricingAnchor?.nativeElement;
      if (!el) {
        return;
      }
      this.pricingObserver = new IntersectionObserver(
        (entries) => {
          if (this.pricingViewFired) {
            return;
          }
          const visible = entries.some((e) => e.isIntersecting && e.intersectionRatio >= 0.5);
          if (visible) {
            this.pricingViewFired = true;
            this.pricingVisible.emit();
          }
        },
        { threshold: [0.5] },
      );
      this.pricingObserver.observe(el);
    });
  }

  private fetchPlans(resetLoading: boolean): void {
    if (resetLoading) {
      this.plansLoading = true;
      this.plansError = false;
      this.cdr.markForCheck();
    }

    this.fetchSub?.unsubscribe();
    const emptyAddons = <T>() =>
      of({ status: 200 as const, count: 0, plans: [] as T[] });

    this.fetchSub = forkJoin({
      subscription: this.invoiceSubsV2.getPublicSubscriptionPlans(),
      docExt: this.invoiceSubsV2
        .getPublicDocExtensionPlans()
        .pipe(catchError(() => emptyAddons<PublicDocExtensionPlanItem>())),
      companyExt: this.invoiceSubsV2
        .getPublicCompanyExtensionPlans()
        .pipe(catchError(() => emptyAddons<PublicCompanyExtensionPlanItem>())),
      tokenExt: this.invoiceSubsV2
        .getPublicTokenExtensionPlans()
        .pipe(catchError(() => emptyAddons<PublicTokenExtensionPlanItem>())),
    }).subscribe({
      next: ({ subscription }) => {
        const mapped = (subscription.plans ?? []).map((p) =>
          this.mapPublicSubscriptionPlan(p),
        );
        this.plans = this.normalizePlans(mapped);
        this.popularPlanIndex = this.computePopularIndex(this.plans);
        this.comparisonMetricRows = this.buildComparisonRows(this.plans);
        this.selectedComparisonIndex = this.popularPlanIndex;
        this.plansLoading = false;
        this.plansError = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.plans = [];
        this.comparisonMetricRows = [];
        this.plansLoading = false;
        this.plansError = true;
        this.cdr.markForCheck();
      },
    });
  }

  private mapPublicSubscriptionPlan(
    p: PublicSubscriptionPlanItem,
  ): InvoiceSubscriptionPlan {
    return {
      id: p.id,
      name_ar: p.name_ar ?? '',
      name_en: p.name_en ?? '',
      name_he: p.name_he ?? '',
      description_ar: p.description_ar,
      description_en: p.description_en,
      description_he: p.description_he,
      documents_limit: p.documents_limit,
      companies_limit: p.companies_limit,
      duration_days: p.duration_days,
      price: p.price,
      is_active: true,
      analysis_tokens_limit: p.analysis_tokens_limit,
      sort_order: p.id,
    };
  }

  private numericPrice(price: number | string): number {
    if (typeof price === 'number') {
      return price;
    }
    const n = parseFloat(String(price).replace(',', '.'));
    return Number.isFinite(n) ? n : NaN;
  }

  private pricePeriodSuffix(durationDays: number): string {
    const L = this.landingLang;
    const ar = L === 'ar';
    const he = L === 'he';
    if (durationDays >= 365 && durationDays % 365 === 0) {
      const years = durationDays / 365;
      if (years <= 1) {
        return ar ? '/سنة' : he ? '/שנה' : '/year';
      }
      return ar ? `/${years} سنوات` : he ? `/${years} שנים` : `/${years} yrs`;
    }
    if (durationDays >= 30 && durationDays % 30 === 0) {
      const months = durationDays / 30;
      return ar ? `/${months} شهر` : he ? `/${months} חודשים` : `/${months} mo`;
    }
    return ar ? `/${durationDays} يوم` : he ? `/${durationDays} ימים` : `/${durationDays} d`;
  }

  private formatDurationDays(days: number): string {
    const L = this.landingLang;
    const ar = L === 'ar';
    const he = L === 'he';
    if (days >= 365 && days % 365 === 0) {
      const y = days / 365;
      if (y <= 1) {
        return ar ? 'سنة واحدة' : he ? 'שנה אחת' : '1 year';
      }
      return ar ? `${y} سنوات` : he ? `${y} שנים` : `${y} years`;
    }
    if (days >= 30 && days % 30 === 0) {
      const m = days / 30;
      return ar ? `${m} شهرًا` : he ? `${m} חודשים` : `${m} months`;
    }
    return String(days);
  }

  private normalizePlans(rows: InvoiceSubscriptionPlan[]): InvoiceSubscriptionPlan[] {
    const list = (rows ?? []).filter((p) => p.is_active !== false);
    list.sort((a, b) => {
      const sa = a.sort_order ?? a.id;
      const sb = b.sort_order ?? b.id;
      return sa - sb;
    });
    return list;
  }

  private computePopularIndex(plans: InvoiceSubscriptionPlan[]): number {
    const n = plans.length;
    if (n <= 0) {
      return 0;
    }
    return Math.min(n - 1, Math.max(0, Math.floor(n / 2)));
  }

  private buildComparisonRows(plans: InvoiceSubscriptionPlan[]): ComparisonMetricRow[] {
    const L = this.landingLang;
    const ar = L === 'ar';
    const he = L === 'he';

    const docLabel = ar ? 'مستندات الفاتورة' : he ? 'מסמכי חשבונית' : 'Invoice documents';
    const coLabel = ar ? 'الشركات' : he ? 'חברות' : 'Companies';
    const tokLabel = ar ? 'رموز التحليل' : he ? 'טוקני ניתוח' : 'Analysis tokens';

    return [
      {
        label: docLabel,
        cells: plans.map((p) => String(p.documents_limit ?? '—')),
      },
      {
        label: coLabel,
        cells: plans.map((p) => String(p.companies_limit ?? '—')),
      },
      {
        label: tokLabel,
        cells: plans.map((p) =>
          p.analysis_tokens_limit != null ? String(p.analysis_tokens_limit) : '—',
        ),
      },
      {
        label: ar ? 'المدة' : he ? 'משך' : 'Duration',
        cells: plans.map((p) => this.formatDurationDays(p.duration_days)),
      },
      {
        label: ar ? 'السعر' : he ? 'מחיר' : 'Price',
        cells: plans.map((p) => {
          const n = this.numericPrice(p.price);
          return Number.isFinite(n)
            ? n.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : '—';
        }),
      },
    ];
  }
}
